import { motion } from "motion/react";
import FullLoading from "../../components/FullLoading";
import { useMemo, useState } from "react";
import EventCardWithTickets from "./components/EventCardWithTickets";
import Button from "../../components/Button";
import { CalendarIcon, StarIcon } from "@heroicons/react/24/outline";
import { ArrowDownTrayIcon } from "@heroicons/react/24/solid";
import CreateEventButton from "./components/CreateEventButton";
import { YESTERDAY } from "../../lib/utils";
import TabBar, { Tab } from "../../components/TabBar";
import useOrganizationEventsPreview from "../../hooks/useOrganizationEventsPreview";
import useOrganizationHasIntegrations from "../../hooks/useOrganizationHasIntegrations";
import PimsImportModal from "../projects/components/PimsImportModal";
import SectionEmptyState from "../../components/SectionEmptyState";

const tabs: Tab[] = [
  {
    label: "À venir",
    value: "upcoming",
    icon: <CalendarIcon className="w-4 h-4" />,
  },
  {
    label: "Passées",
    value: "past",
    icon: <CalendarIcon className="w-4 h-4" />,
  },
];

export default function Events() {
  const { hasPimsIntegration } = useOrganizationHasIntegrations();
  const [activeTab, setActiveTab] = useState<Tab>(tabs[0]);
  const [isImportEventsModalOpen, setIsImportEventsModalOpen] = useState(false);

  const { data, loading, error } = useOrganizationEventsPreview({
    afterDate: activeTab.value === "upcoming" ? YESTERDAY : null,
    beforeDate: activeTab.value === "past" ? YESTERDAY : null,
  });

  // Fetch both upcoming and past events to check if there are any events at all
  const { data: upcomingData } = useOrganizationEventsPreview({
    afterDate: YESTERDAY,
    beforeDate: null,
  });

  const { data: pastData } = useOrganizationEventsPreview({
    afterDate: null,
    beforeDate: YESTERDAY,
  });

  const hasAnyEvents = useMemo(() => {
    const hasUpcoming = (upcomingData?.events?.length ?? 0) > 0;
    const hasPast = (pastData?.events?.length ?? 0) > 0;
    return hasUpcoming || hasPast;
  }, [upcomingData?.events, pastData?.events]);

  // const [updateEventParticipants] = useMutation(
  //   UpdateEventParticipantsDocument
  // );

  if (loading) return <FullLoading />;

  if (error) return <div>Error: {error.message}</div>;

  // TODO: Search input
  // TODO: Filter by city

  const tabComponent = () => {
    return (
      <TabBar tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab} />
    );
  };

  const pageHeader = () => {
    return (
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-primary">Évènements</h1>
        <div className="flex gap-2">
          {hasPimsIntegration && (
            <Button
              variant="secondary"
              className="text-sm"
              onClick={() => {
                setIsImportEventsModalOpen(true);
              }}
            >
              <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
              Importer des évènements
            </Button>
          )}
          <CreateEventButton variant="secondary" />
        </div>
      </div>
    );
  };

  const emptyState = () => {
    return (
      <SectionEmptyState
        icon={<StarIcon className="w-10 h-10 text-accent" />}
        title="Évènements"
        description={
          <div>
            Créez et organisez vos{" "}
            <span className="text-accent font-medium">évènements</span> :
            spectacles, concerts, avant-premières etc. Ajoutez des participants,
            des documents et suivez tous les détails de vos évènements.
          </div>
        }
        actionButton={<CreateEventButton />}
      />
    );
  };

  const pageContent = () => {
    // If there are no events at all, show the empty state
    if (!hasAnyEvents) {
      return emptyState();
    }

    // If current tab has no events but other tab does, show simple message
    if (data?.events.length === 0 && activeTab.value === "upcoming") {
      return (
        <div className="text-secondary text-sm">Pas d'évènements à venir</div>
      );
    }

    if (data?.events.length === 0 && activeTab.value === "past") {
      return (
        <div className="text-secondary text-sm">Pas d'évènements passés</div>
      );
    }

    // Events are sorted by default from the backend but in the case of refetching because of notifcation
    // we need to sort them again
    const sortedEvents = data?.events
      .filter((event) => event !== null)
      .sort((a, b) => {
        return (
          new Date(a?.startDate).getTime() - new Date(b?.startDate).getTime()
        );
      });

    return (
      <div className="flex flex-wrap gap-4">
        {sortedEvents?.map((event, index) => {
          if (!event) return null;

          return (
            <EventCardWithTickets key={`${event.id}-${index}`} event={event} />
          );
        })}
      </div>
    );
  };

  return (
    <motion.div
      className=" flex flex-col gap-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      data-testid="events-page"
    >
      <PimsImportModal
        isOpen={isImportEventsModalOpen}
        onClose={() => setIsImportEventsModalOpen(false)}
        mode="events"
      />

      {hasAnyEvents ? pageHeader() : null}

      {hasAnyEvents ? tabComponent() : null}

      <div className="mt-8">{pageContent()}</div>
    </motion.div>
  );
}
